


























import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { CorpBasic } from '@/adapters/corp/GetCorpBasic'
import { CorpBasicCache, CorpSignupType } from '@/presentation/corp/CorpBasicCache'

@Component
export default class CorpPicker extends Vue {
  @Prop({ type: Array, default: () => [] }) value!: Array<number>
  @Prop({ type: String, default: 'remittance' }) signupType!: string
  @Prop({ type: Number, default: 50 }) maxVisibleOptions!: number
  @Prop({ type: String, default: '고객사 검색 (corp_id 붙여넣기 가능)' }) placeholder!: string

  corpList: Array<CorpBasic> = []
  corpMap: Map<number, string> = new Map()
  loading = false
  query = ''

  get visibleOptions (): Array<CorpBasic> {
    const query = this.query.trim()
    let matched: Array<CorpBasic>
    if (!query) {
      matched = this.corpList.slice(0, this.maxVisibleOptions)
    } else if (/^\d+$/.test(query)) {
      matched = this.corpList.filter(corp => `${corp.corp_id}`.startsWith(query)).slice(0, this.maxVisibleOptions)
    } else {
      const lowerCaseQuery = query.toLowerCase()
      matched = this.corpList
        .filter(corp => corp.corp_name.toLowerCase().includes(lowerCaseQuery))
        .slice(0, this.maxVisibleOptions)
    }
    const matchedIds = new Set(matched.map(corp => corp.corp_id))
    const selectedOptions: Array<CorpBasic> = this.value
      .filter(corpId => !matchedIds.has(corpId))
      .map(corpId => ({ corp_id: corpId, corp_name: this.corpMap.get(corpId) || '' }))
    return matched.concat(selectedOptions)
  }

  getCorpLabel (corp: CorpBasic): string {
    return corp.corp_name ? `${corp.corp_name} (${corp.corp_id})` : `${corp.corp_id}`
  }

  onFilterQuery (query: string) {
    this.query = query || ''
  }

  onVisibleChange (visible: boolean) {
    if (!visible) this.query = ''
  }

  onInput (value: Array<number>) {
    this.$emit('input', value)
  }

  onChange (value: Array<number>) {
    this.$emit('change', value)
  }

  onPaste (event: ClipboardEvent) {
    const pastedText = event.clipboardData ? event.clipboardData.getData('text') : ''
    const isCorpIdListPattern = /^[\d\s,;]+$/.test(pastedText) && /\d/.test(pastedText)
    if (!isCorpIdListPattern) return
    event.preventDefault()
    const pastedIds = pastedText
      .split(/[\s,;]+/)
      .filter(idText => !!idText)
      .map(idText => Number(idText))
      .filter(corpId => Number.isFinite(corpId) && corpId > 0)
    const uniqueIds = Array.from(new Set(pastedIds))
    const knownIds = uniqueIds.filter(corpId => this.corpMap.has(corpId))
    const unknownIds = uniqueIds.filter(corpId => !this.corpMap.has(corpId))
    if (unknownIds.length) this.$message.warning(`존재하지 않는 corp_id: ${unknownIds.join(', ')}`)
    if (!knownIds.length) return
    const mergedIds = Array.from(new Set(this.value.concat(knownIds)))
    this.$emit('input', mergedIds)
    this.$emit('change', mergedIds)
    this.$nextTick(() => {
      // el-select resolves chip labels from options already rendered; ids pasted while the
      // filter input keeps focus skip that resolution, so re-run it after options render
      const select = this.$refs.corpSelect as Vue & { setSelected?: () => void }
      if (select && select.setSelected) select.setSelected()
    })
  }

  async fetchCorps () {
    this.loading = true
    try {
      const corpBasicCache = CorpBasicCache.getInstance()
      this.corpList = await corpBasicCache.get(this.signupType as CorpSignupType)
      this.corpMap = await corpBasicCache.getCorpMap(this.signupType as CorpSignupType)
    } finally {
      this.loading = false
    }
  }

  async mounted () {
    this.$el.addEventListener('paste', this.onPaste as EventListener)
    try {
      await this.fetchCorps()
    } catch (error) {
      this.$message.error('고객사 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
    }
  }

  beforeDestroy () {
    this.$el.removeEventListener('paste', this.onPaste as EventListener)
  }
}
